'use client'

import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

interface RollingDigitProps {
  digit: number
  digitHeight?: number
  duration?: number
}

/**
 * RollingDigit Component
 *
 * This component handles one digit and animates a roll from the previous value
 * to the new one. For upward changes the sequence is built from prevDigit to new digit,
 * and the list slides upward. For downward changes the sequence is built from new digit
 * to prevDigit, and the list starts hidden above and slides down.
 *
 * Props:
 * - digit: The new digit (number) to display.
 * - digitHeight: The height in pixels for each digit (default is 40).
 * - duration: Duration of the animation in seconds (default is 0.8).
 */
const RollingDigit: React.FC<RollingDigitProps> = ({
  digit,
  digitHeight = 40,
  duration = 0.8
}) => {
  // Store the previous digit so we know where to start from.
  const [prevDigit, setPrevDigit] = useState<number>(digit)
  // Flag to track if we're animating.
  const [animating, setAnimating] = useState<boolean>(false)

  // When the digit changes, trigger the animation.
  useEffect(() => {
    if (digit !== prevDigit) {
      setAnimating(true)
      const timeout = setTimeout(() => {
        setPrevDigit(digit)
        setAnimating(false)
      }, duration * 1000)
      return () => clearTimeout(timeout)
    }
  }, [digit, prevDigit, duration])

  // If not animating, just render the current digit.
  if (!animating) {
    return (
      <div
        style={{
          height: digitHeight,
          lineHeight: `${digitHeight}px`,
          display: 'inline-block',
          textAlign: 'center'
        }}
      >
        {digit}
      </div>
    )
  }

  // Check the direction of the change.
  if (digit > prevDigit) {
    // UPWARD ROLL:
    // Build a sequence from the previous digit to the new digit.
    const sequence: number[] = []
    for (let i = prevDigit; i <= digit; i++) {
      sequence.push(i)
    }
    const steps = sequence.length - 1
    // For upward, we slide upward (negative y offset).
    const finalOffset = -steps * digitHeight
    return (
      <div
        style={{
          overflow: 'hidden',
          height: digitHeight,
          display: 'inline-block',
          verticalAlign: 'top'
        }}
      >
        <motion.div
          initial={{ y: 0 }}
          animate={{ y: finalOffset }}
          transition={{ duration, ease: 'easeInOut' }}
        >
          {sequence.map((num, index) => (
            <div
              key={index}
              style={{
                height: digitHeight,
                lineHeight: `${digitHeight}px`,
                textAlign: 'center'
              }}
            >
              {num % 10}
            </div>
          ))}
        </motion.div>
      </div>
    )
  } else {
    // DOWNWARD ROLL:
    // Build a sequence from the new digit up to the previous digit.
    const sequence: number[] = []
    for (let i = digit; i <= prevDigit; i++) {
      sequence.push(i)
    }
    const steps = sequence.length - 1
    // For downward, we want the new digit (at the top) to slide into view.
    const initialOffset = -steps * digitHeight
    return (
      <div
        style={{
          overflow: 'hidden',
          height: digitHeight,
          display: 'inline-block',
          verticalAlign: 'top'
        }}
      >
        <motion.div
          // Start with the new digit hidden above.
          initial={{ y: initialOffset }}
          // Animate down to bring it into view.
          animate={{ y: 0 }}
          transition={{ duration, ease: 'easeInOut' }}
        >
          {sequence.map((num, index) => (
            <div
              key={index}
              style={{
                height: digitHeight,
                lineHeight: `${digitHeight}px`,
                textAlign: 'center'
              }}
            >
              {num % 10}
            </div>
          ))}
        </motion.div>
      </div>
    )
  }
}

interface AnimatedNumberProps {
  value: number
  formatOptions?: Intl.NumberFormatOptions
  className?: string
  digitHeight?: number
  duration?: number
}

/**
 * AnimatedNumber Component
 *
 * This component takes a number, formats it (using Intl.NumberFormat),
 * and splits it into characters. For each digit character, it uses the RollingDigit
 * component to animate the roll. Non-digit characters (such as commas or decimals)
 * are rendered statically.
 *
 * Props:
 * - value: The number to display.
 * - formatOptions: Options for formatting the number (optional).
 * - className: Additional CSS classes (optional).
 * - digitHeight: Height for each digit (passed to RollingDigit).
 * - duration: Duration for the roll animation (passed to RollingDigit).
 */
export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  formatOptions,
  className,
  digitHeight = 40,
  duration = 0.8
}) => {
  // Format the number (e.g., "1,234.56")
  const formattedValue: string = new Intl.NumberFormat(
    'en-US',
    formatOptions
  ).format(value)
  // Split the formatted number into individual characters.
  const characters: string[] = formattedValue.split('')

  return (
    <span className={className} style={{ display: 'inline-flex' }}>
      {characters.map((char, index) => {
        // If the character is a digit, animate it.
        if (/\d/.test(char)) {
          return (
            <RollingDigit
              key={index}
              digit={parseInt(char, 10)}
              digitHeight={digitHeight}
              duration={duration}
            />
          )
        } else {
          // Otherwise, just display the character.
          return (
            <span
              key={index}
              style={{
                height: digitHeight,
                lineHeight: `${digitHeight}px`,
                display: 'inline-block'
              }}
            >
              {char}
            </span>
          )
        }
      })}
    </span>
  )
}
